import React, { useState, useEffect, useMemo } from 'react';
import { marketingApiService } from '../../marketing-v2/services/marketingApiService';
import { buildSampleEmails, buildSampleSms, buildSampleAutomationStats, SampleDataBanner, SampleDataPrompt } from './sampleCampaigns';
import DateRangePicker from '../../finance/components/DateRangePicker';
import { ChevronDownIcon } from '../../finance/icons/FinanceIcons';
import { getDatePresetLabel } from '../../finance/data/mockData';
import './Campaigns.css';

/**
 * Marketing › Performance as it was before the rebuild
 * (marketing-v2/features/performance), shown while PERFORMANCE_V2_VISIBLE is
 * off. At launch, delete this file and the .perf- styles in Campaigns.css.
 *
 * Account-level outreach analytics: engagement, funnel, campaign
 * leaderboard, and delivery health, aggregated over the chosen period.
 * Aggregates come from the campaigns list (real stats on sent campaigns);
 * getAnalyticsOverview is consulted first and used when the backend
 * provides it.
 */

const pct = (num, den) => (num && den ? Math.round((num / den) * 100) : null);

// Window filtering against the shared DateRangePicker's explicit dates.
// A null start/end means no bound (the 'All time' preset).
const endOfDay = (d) => { const e = new Date(d); e.setHours(23, 59, 59, 999); return e.getTime(); };
const inWindow = (c, start, end) => {
  const when = c.sentDate ? new Date(c.sentDate).getTime() : null;
  if (!when) return false;
  if (start && when < start.getTime()) return false;
  if (end && when > endOfDay(end)) return false;
  return true;
};
const previousWindow = (start, end) => {
  if (!start || !end) return null;
  const length = endOfDay(end) - start.getTime();
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(start.getTime() - 1 - length);
  return { start: prevStart, end: prevEnd };
};

const aggregate = (campaigns) => {
  const sum = (k) => campaigns.reduce((n, c) => n + (Number(c.stats?.[k]) || 0), 0);
  const sent = sum('sent');
  const delivered = sum('delivered');
  const opened = sum('opened');
  const clicked = sum('clicked');
  const bookings = sum('bookings');
  return {
    campaigns: campaigns.length,
    sent,
    delivered,
    opened,
    clicked,
    bookings,
    deliveryRate: pct(delivered, sent),
    openRate: pct(opened, delivered),
    clickRate: pct(clicked, delivered),
  };
};

const Delta = ({ now, prev, suffix = '' }) => {
  if (prev === null || prev === undefined || now === null || now === undefined || (!prev && !now)) return null;
  const diff = now - prev;
  if (!diff) return <span className="perf-delta flat">No change</span>;
  return (
    <span className={`perf-delta ${diff > 0 ? 'up' : 'down'}`}>
      {diff > 0 ? '▲' : '▼'} {Math.abs(diff).toLocaleString()}{suffix} vs previous period
    </span>
  );
};

const FunnelBar = ({ label, value, base, color }) => {
  const width = base ? Math.max(2, Math.round((value / base) * 100)) : 0;
  return (
    <div className="perf-funnel-row">
      <span className="perf-funnel-label">{label}</span>
      <div className="perf-funnel-track">
        <div className="perf-funnel-fill" style={{ width: `${width}%`, background: color }} />
      </div>
      <span className="perf-funnel-value">
        {Number(value || 0).toLocaleString()}
        {base && value ? <em> · {Math.round((value / base) * 100)}%</em> : null}
      </span>
    </div>
  );
};

function LegacyPerformance({ dispatch, history }) {
  // Shared custom date filter (same one Finance uses)
  const [datePreset, setDatePreset] = useState('last4weeks');
  const [dateStart, setDateStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 28); d.setHours(0, 0, 0, 0); return d; });
  const [dateEnd, setDateEnd] = useState(() => new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [sms, setSms] = useState([]);
  const [overview, setOverview] = useState(null);
  const [automationStats, setAutomationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [e, s] = await Promise.all([
          marketingApiService.getCampaigns(dispatch, { type: 'email' }).catch(() => []),
          marketingApiService.getCampaigns(dispatch, { type: 'sms' }).catch(() => []),
        ]);
        if (!alive) return;
        setEmails((e || []).filter((c) => c.status === 'sent'));
        setSms((s || []).filter((c) => c.status === 'sent'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    // Backend overview, when available, refines the client-side aggregates
    marketingApiService.getAnalyticsOverview(dispatch, {})
      .then((res) => { if (alive && res && typeof res === 'object') setOverview(res); })
      .catch(() => {});
    // Automated sends by kind and per journey (analytics/automations)
    marketingApiService.getAutomationStats(dispatch, {})
      .then((res) => { if (alive && res && Array.isArray(res.automations)) setAutomationStats(res); })
      .catch(() => {});
    return () => { alive = false; };
  }, [dispatch]);

  const effectiveEmails = demoMode ? buildSampleEmails() : emails;
  const effectiveSms = demoMode ? buildSampleSms() : sms;
  const effectiveAutomations = demoMode ? buildSampleAutomationStats() : automationStats;

  const prevWin = previousWindow(dateStart, dateEnd);
  const current = useMemo(() => aggregate(effectiveEmails.filter((c) => inWindow(c, dateStart, dateEnd))), [effectiveEmails, dateStart, dateEnd]);
  const previous = useMemo(() => (prevWin ? aggregate(effectiveEmails.filter((c) => inWindow(c, prevWin.start, prevWin.end))) : null), [effectiveEmails, dateStart, dateEnd]); // eslint-disable-line react-hooks/exhaustive-deps
  const smsTotals = useMemo(() => aggregate(effectiveSms.filter((c) => inWindow(c, dateStart, dateEnd))), [effectiveSms, dateStart, dateEnd]);

  // Activity over time: a CONTINUOUS axis across the window (empty periods
  // included), weekly buckets for short ranges and monthly for long ones, so
  // sparse data reads as a real chart rather than floating bars.
  const overTime = useMemo(() => {
    const inWin = effectiveEmails.filter((c) => inWindow(c, dateStart, dateEnd) && c.stats && c.sentDate);
    const times = inWin.map((c) => new Date(c.sentDate).getTime());
    const end = dateEnd ? endOfDay(dateEnd) : Date.now();
    let start = dateStart ? dateStart.getTime() : (times.length ? Math.min(...times) : end);
    if (start > end) start = end;
    const spanDays = (end - start) / 864e5;
    const weekly = spanDays <= 70;

    const buckets = [];
    if (weekly) {
      // Week starting Monday
      const cur = new Date(start);
      cur.setHours(0, 0, 0, 0);
      cur.setDate(cur.getDate() - ((cur.getDay() + 6) % 7));
      while (cur.getTime() <= end && buckets.length < 30) {
        const from = cur.getTime();
        const to = from + 7 * 864e5;
        buckets.push({
          from,
          to,
          label: new Date(from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
          delivered: 0,
          bookings: 0,
        });
        cur.setDate(cur.getDate() + 7);
      }
    } else {
      const cur = new Date(start);
      cur.setHours(0, 0, 0, 0);
      cur.setDate(1);
      while (cur.getTime() <= end && buckets.length < 30) {
        const from = cur.getTime();
        const next = new Date(cur);
        next.setMonth(next.getMonth() + 1);
        buckets.push({
          from,
          to: next.getTime(),
          label: cur.toLocaleDateString('en-GB', spanDays > 360 ? { month: 'short', year: '2-digit' } : { month: 'short' }),
          delivered: 0,
          bookings: 0,
        });
        cur.setMonth(cur.getMonth() + 1);
      }
    }

    inWin.forEach((c) => {
      const t = new Date(c.sentDate).getTime();
      const b = buckets.find((x) => t >= x.from && t < x.to);
      if (b) {
        b.delivered += Number(c.stats.delivered) || 0;
        b.bookings += Number(c.stats.bookings) || 0;
      }
    });
    // Keep the MOST RECENT buckets when capping, so the current period
    // never falls off the end of the chart
    return buckets.slice(weekly ? -14 : -12);
  }, [effectiveEmails, dateStart, dateEnd]);

  const leaderboard = useMemo(() => (
    effectiveEmails
      .filter((c) => inWindow(c, dateStart, dateEnd) && c.stats)
      .sort((a, b) => (b.stats?.bookings || 0) - (a.stats?.bookings || 0)
        || (pct(b.stats?.clicked, b.stats?.delivered) || 0) - (pct(a.stats?.clicked, a.stats?.delivered) || 0))
      .slice(0, 6)
  ), [effectiveEmails, dateStart, dateEnd]);

  const hasData = current.campaigns > 0 || smsTotals.campaigns > 0;
  // Surface backend-provided revenue when the overview endpoint returns it
  const attributedRevenue = demoMode ? 1840 : (overview?.attributedRevenue ?? overview?.revenue ?? null);

  return (
    <div className="cmp-wrap">
      <div className="cmp-head">
        <div className="cmp-head-text">
          <h1 className="cmp-title">Performance</h1>
          <p className="cmp-subtitle">How your campaigns are landing, and what they bring back through the door.</p>
        </div>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setDatePickerOpen((o) => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '11px 16px', backgroundColor: '#fff',
              border: '1px solid #ECECF0', borderRadius: 10,
              fontSize: 14, fontWeight: 500, color: '#007EA0',
              cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap'
            }}
          >
            {datePreset ? getDatePresetLabel(datePreset)
              : `${dateStart ? dateStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '…'} – ${dateEnd ? dateEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '…'}`}
            <ChevronDownIcon size={16} />
          </button>
          <DateRangePicker
            isOpen={datePickerOpen}
            onClose={() => setDatePickerOpen(false)}
            startDate={dateStart}
            endDate={dateEnd}
            selectedPreset={datePreset}
            onPresetChange={setDatePreset}
            align="right"
            onApply={(start, end) => {
              setDateStart(start);
              setDateEnd(end);
            }}
          />
        </div>
      </div>

      {demoMode && <SampleDataBanner onExit={() => setDemoMode(false)} />}
      {!demoMode && !loading && emails.length === 0 && sms.length === 0 && (
        <SampleDataPrompt onPreview={() => setDemoMode(true)} />
      )}

      {loading ? (
        <div className="cmp-kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className="cmp-skel" style={{ height: 96, borderRadius: 14 }} />)}
        </div>
      ) : !hasData ? (
        <div className="cmp-empty">
          <div className="cmp-empty-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M7 14l4-4 3 3 5-6" />
            </svg>
          </div>
          <h3 className="cmp-empty-title">No results yet</h3>
          <p className="cmp-empty-text">Send your first campaign and its performance will appear here, from opens through to bookings.</p>
          <button className="cmp-new-btn" onClick={() => history.push('/marketing-campaigns')}>Go to campaigns</button>
        </div>
      ) : (
        <>
          {/* Engagement */}
          <div className="cmp-kpis">
            <div className="cmp-kpi">
              <p className="cmp-kpi-label">Emails delivered</p>
              <p className="cmp-kpi-num">{current.delivered.toLocaleString()}</p>
              {previous && <Delta now={current.delivered} prev={previous.delivered} />}
            </div>
            <div className="cmp-kpi">
              <p className="cmp-kpi-label">Open rate</p>
              <p className="cmp-kpi-num">{current.openRate !== null ? `${current.openRate}%` : '·'}</p>
              {previous && <Delta now={current.openRate} prev={previous.openRate} suffix="%" />}
            </div>
            <div className="cmp-kpi">
              <p className="cmp-kpi-label">Click rate</p>
              <p className="cmp-kpi-num">{current.clickRate !== null ? `${current.clickRate}%` : '·'}</p>
              {previous && <Delta now={current.clickRate} prev={previous.clickRate} suffix="%" />}
            </div>
            <div className="cmp-kpi">
              <p className="cmp-kpi-label">Bookings generated</p>
              <p className="cmp-kpi-num">{current.bookings.toLocaleString()}</p>
              {previous && <Delta now={current.bookings} prev={previous.bookings} />}
            </div>
          </div>

          {attributedRevenue !== null && (
            <div className="perf-revenue">
              <div>
                <p className="perf-revenue-label">Revenue attributed to marketing</p>
                <p className="perf-revenue-sub">Bookings and sales within 72 hours of a campaign click</p>
              </div>
              <p className="perf-revenue-num">£{Number(attributedRevenue).toLocaleString()}</p>
            </div>
          )}

          <div className="perf-grid">
            {/* Funnel */}
            <div className="perf-card">
              <p className="perf-card-title">Marketing funnel</p>
              <FunnelBar label="Sent" value={current.sent} base={current.sent} color="#29282D" />
              <FunnelBar label="Delivered" value={current.delivered} base={current.sent} color="#4B4B52" />
              <FunnelBar label="Opened" value={current.opened} base={current.sent} color="#007EA0" />
              <FunnelBar label="Clicked" value={current.clicked} base={current.sent} color="#00ACC9" />
              <FunnelBar label="Booked" value={current.bookings} base={current.sent} color="#005F79" />
            </div>

            {/* Delivery health + SMS */}
            <div className="perf-card">
              <p className="perf-card-title">Delivery health</p>
              <div className="perf-health-row">
                <span>Email delivery rate</span>
                <strong>{current.deliveryRate !== null ? `${current.deliveryRate}%` : '·'}</strong>
              </div>
              <div className="perf-health-row">
                <span>Email campaigns sent</span>
                <strong>{current.campaigns}</strong>
              </div>
              <div className="perf-health-row">
                <span>SMS blasts sent</span>
                <strong>{smsTotals.campaigns}</strong>
              </div>
              <div className="perf-health-row">
                <span>SMS delivery rate</span>
                <strong>{smsTotals.deliveryRate !== null ? `${smsTotals.deliveryRate}%` : '·'}</strong>
              </div>
              <p className="perf-footnote">Bounce and unsubscribe breakdowns arrive with the upgraded sending engine.</p>
            </div>
          </div>

          {/* Activity over time */}
          {overTime.length > 1 && overTime.some((b) => b.delivered > 0) && (
            <div className="perf-card" style={{ marginTop: 14 }}>
              <p className="perf-card-title">{overTime.length && overTime[0].to - overTime[0].from <= 8 * 864e5 ? 'Activity by week' : 'Activity by month'}</p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 132, padding: '0 4px' }}>
                {overTime.map((m, i) => {
                  const max = Math.max(...overTime.map((x) => x.delivered), 1);
                  const h = m.delivered > 0 ? Math.max(8, Math.round((m.delivered / max) * 86)) : 3;
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 4, minWidth: 0, height: '100%' }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: m.delivered > 0 ? '#1A1A1E' : '#C7C7CC', whiteSpace: 'nowrap', letterSpacing: '-0.2px' }}>
                        {m.delivered > 0 ? m.delivered.toLocaleString() : '0'}
                      </span>
                      <div
                        style={{
                          width: '100%',
                          maxWidth: 38,
                          height: h,
                          background: m.delivered > 0 ? 'linear-gradient(180deg, #00ACC9, #007EA0)' : '#E5E5EA',
                          borderRadius: m.delivered > 0 ? '6px 6px 2px 2px' : 2
                        }}
                        title={`${m.delivered.toLocaleString()} delivered · ${m.bookings || 0} booked`}
                      />
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: '#8E8E93', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{m.label}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: m.bookings > 0 ? '#007EA0' : 'transparent', whiteSpace: 'nowrap', lineHeight: 1 }}>
                        {m.bookings > 0 ? `${m.bookings} booked` : '.'}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="perf-footnote">Emails delivered each period, with bookings generated underneath.</p>
            </div>
          )}

          {/* Automations: reminders, boosters and custom sends */}
          {effectiveAutomations && Array.isArray(effectiveAutomations.automations) && effectiveAutomations.automations.length > 0 && (
            <div className="perf-card" style={{ marginTop: 14 }}>
              <p className="perf-card-title">Automations</p>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: '480px' }}>
                  <div className="perf-table-head" style={{ gridTemplateColumns: '1.8fr 0.7fr 0.7fr 0.6fr 0.7fr' }}>
                    <span>Automation</span><span className="r">Emails</span><span className="r">Texts</span><span className="r">Opens</span><span className="r">Outcomes</span>
                  </div>
                  {effectiveAutomations.automations.map((a, i) => {
                    const sends = (Number(a.emailSent) || 0) + (Number(a.smsSent) || 0);
                    const openRate = pct(a.opened, a.emailSent);
                    return (
                      <div
                        key={a.journeyId || a.type || i}
                        className="perf-table-row"
                        style={{ gridTemplateColumns: '1.8fr 0.7fr 0.7fr 0.6fr 0.7fr', cursor: 'pointer' }}
                        onClick={() => (a.type === 'journey'
                          ? history.push('/marketing-journeys')
                          : history.push('/marketing', { openSection: ({ reminder: '_reminders', bookingUpdate: '_updates', salesUpdate: '_sales', paymentLink: '_sales', reviewBooster: '_reviewbooster', customUpdate: '_custom' })[a.type] || '_custom' }))}
                        title={a.type === 'journey' ? 'Open Journeys' : 'Open in Communications'}
                      >
                        <span className="perf-table-name">{a.name || a.type}</span>
                        <span className="r">{Number(a.emailSent || 0).toLocaleString()}</span>
                        <span className="r">{Number(a.smsSent || 0).toLocaleString()}</span>
                        <span className="r">{openRate !== null ? `${openRate}%` : '·'}</span>
                        <span className={`r${a.conversions ? ' pos' : ''}`}>
                          {a.conversions ? `${a.conversions} reviews` : a.clicked ? `${a.clicked} clicks` : sends ? '·' : '·'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="perf-footnote">Automated reminders, updates and boosters sent in the period. Open rates cover email only.</p>
            </div>
          )}

          {/* Campaign leaderboard */}
          <div className="perf-card" style={{ marginTop: 14 }}>
            <p className="perf-card-title">Top campaigns</p>
            {leaderboard.length === 0 ? (
              <p className="perf-footnote" style={{ marginTop: 4 }}>No sent campaigns in this period.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: '540px' }}>
                <div className="perf-table-head">
                  <span>Campaign</span><span className="r">Delivered</span><span className="r">Opens</span><span className="r">Clicks</span><span className="r">Bookings</span>
                </div>
                {leaderboard.map((c) => (
                  <div
                    key={c.id}
                    className="perf-table-row"
                    style={c._demo ? undefined : { cursor: 'pointer' }}
                    onClick={() => { if (!c._demo) history.push('/marketing-campaigns', { openCampaignId: c.id }); }}
                    title={c._demo ? undefined : 'Open campaign'}
                  >
                    <span className="perf-table-name">
                      {c.name}
                      <em>{c.sentDate ? new Date(c.sentDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</em>
                    </span>
                    <span className="r">{Number(c.stats?.delivered || 0).toLocaleString()}</span>
                    <span className="r">{pct(c.stats?.opened, c.stats?.delivered) !== null ? `${pct(c.stats?.opened, c.stats?.delivered)}%` : '·'}</span>
                    <span className="r">{pct(c.stats?.clicked, c.stats?.delivered) !== null ? `${pct(c.stats?.clicked, c.stats?.delivered)}%` : '·'}</span>
                    <span className={`r${c.stats?.bookings ? ' pos' : ''}`}>{c.stats?.bookings || '·'}</span>
                  </div>
                ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default LegacyPerformance;
